<template>
    <Slider
        ref="carousel"
        :options="options"
        @slide-change="onSlideChange"
        @autoplay-time-left="onAutoplayTimeLeft"
    >
        <div
            v-for="(slide, slideIndex) in banner?.bannerA_Images"
            :key="slideIndex"
            class="swiper-slide relative min-h-svh w-svw"
        >
            <div
                class="relative min-h-svh bg-cover bg-no-repeat"
                :style="{
                    backgroundImage: `url(${slide?.url})`,
                    backgroundPosition: `${slide?.focalPoint[0] * 100 ?? '50'}% ${slide?.focalPoint[1] * 100 ?? '50'}%`,
                }"
            >
                <!-- overlay -->
                <div
                    v-if="banner?.bannerA_Overlay !== 'off'"
                    class="absolute inset-0 z-0"
                    :style="bannerOverlayStyle"
                />
            </div>
        </div>

        <template #navigation>
            <div class="absolute inset-x-4 bottom-11 z-40 flex items-center justify-between lg:inset-x-12">
                <div
                    class="w-1/3 lg:flex"
                    :class="{
                        hidden: showDiscoverMoreLink,
                    }"
                />

                <div
                    v-if="showDiscoverMoreLink"
                    class="flex w-1/3 items-center justify-center"
                >
                    <div
                        class="text-neutral-base flex items-center space-x-1"
                        @click="scrollToSection"
                    >
                        <span class="text-xs font-medium">Discover more</span>
                        <DIcon icon="ph:caret-circle-down" />
                    </div>
                </div>

                <div class="flex w-1/3 items-center justify-end space-x-2">
                    <template v-if="banner?.bannerA_Images?.length > 1">
                        <div class="bg-white-opacity-20 rounded-button-md flex items-center p-1">
                            <div class="button-prev">
                                <DButton
                                    theme="transparent"
                                    icon="ph:caret-left"
                                    size="sm"
                                />
                            </div>
                            <span class="text-neutral-base px-2 text-xs font-medium">
                                {{ activeIndex + 1 }}/{{ banner?.bannerA_Images?.length }}
                            </span>
                            <div class="button-next">
                                <DButton
                                    theme="transparent"
                                    icon="ph:caret-right"
                                    size="sm"
                                />
                            </div>
                        </div>

                        <div class="bg-white-opacity-20 rounded-button-md flex items-center p-1">
                            <DButton
                                theme="transparent"
                                :icon="`ph:${isAutoplaying ? 'pause' : 'play'}-fill`"
                                size="sm"
                                @click="toggleAutoplay"
                            />
                        </div>
                    </template>
                </div>
            </div>

            <div
                v-if="banner?.bannerA_Images?.length > 1 && banner?.bannerA_ShowLoadingIndicator"
                class="absolute inset-x-0 bottom-0 z-40"
            >
                <DProgress
                    :value="progress"
                    :brand="true"
                    :overlay="true"
                    :max="AUTOPLAY_DURARTION"
                    :animate="false"
                    :rounded="false"
                    type="fill"
                    size="lg"
                />
            </div>
        </template>
    </Slider>
</template>

<script setup>
    import { DButton, DIcon, DProgress } from '@digistorm/spark'

    const AUTOPLAY_DURARTION = 8000

    const props = defineProps({
        banner: Object,
    })

    const options = ref({
        loop: (props.banner?.bannerA_Images?.length || 0) > 1,
        pagination: false,
        autoplay: {
            delay: AUTOPLAY_DURARTION,
        },
        delay: AUTOPLAY_DURARTION,
    })

    const carousel = ref(null)

    const activeIndex = ref(0)
    const progress = ref(0)
    const isAutoplaying = ref(true)

    const onSlideChange = (swiper) => {
        activeIndex.value = swiper.realIndex
        // changing slide resets autoplay
        isAutoplaying.value = true
    }

    const onAutoplayTimeLeft = (e) => {
        progress.value = AUTOPLAY_DURARTION - e.autoplay.timeLeft
    }

    const toggleAutoplay = () => {
        if (isAutoplaying.value) {
            carousel.value?.swiper?.autoplay.pause()
        } else {
            carousel.value?.swiper?.autoplay.resume()
        }

        isAutoplaying.value = !isAutoplaying.value
    }

    const scrollToSection = () => {
        // scroll to first section tag with an offset
        const section = document.querySelector('section')
        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }

    const bannerOverlayStyle = computed(() => {
        return {
            backgroundColor: props.banner.bannerA_Overlay === 'custom' ? props.banner.bannerA_CustomOverlay : '#000000',
            opacity: props.banner.bannerA_OverlayOpacity / 100,
        }
    })

    const showDiscoverMoreLink = computed(() => !get(props.banner, 'bannerA_HideDiscoverMoreLink'))
</script>
